import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Table,
    Badge,
    Button,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import CustomDropdown from "components/Common/CustomDropdown";
import CustomPagination from "components/Common/CustomPagination";
import { getMemberships, getMembershipById } from "api/ApiServices";

const ITEMS_PER_PAGE = 10;

const Membership = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [planFilter, setPlanFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedMember, setSelectedMember] = useState(null);
    const [viewMemberModal, setViewMemberModal] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [viewLoading, setViewLoading] = useState(false);

    // Fetch memberships from API
    const fetchMemberships = async (page = 1, search = "") => {
        setLoading(true);
        try {
            const response = await getMemberships({
                page: page,
                limit: ITEMS_PER_PAGE,
                search: search
            });

            if (response.success) {
                const transformedMembers = response.data.map((membership, index) => ({
                    id: membership._id,
                    uuid: membership.uuid,
                    name: membership.user?.fullName || 'Unknown',
                    email: membership.user?.email || 'N/A',
                    phone: membership.user?.mobileNumber || 'N/A',
                    isActive: membership.isActive,
                    status: membership.status,
                    plan: membership.plan,
                    startDate: membership.startDate,
                    endDate: membership.endDate,
                    createdAt: membership.createdAt,
                    profileImage: membership.user?.profileImage || '',
                    type: membership.user?.type || 'Member',
                    avatar: membership.user?.profileImage
                        ? `/uploads/${membership.user.profileImage}`
                        : null,
                    userUuid: membership.userUuid,
                    user: membership.user,
                }));

                setMembers(transformedMembers);
                setTotalItems(response.pagination?.totalRecords || transformedMembers.length);
                setTotalPages(response.pagination?.totalPages || 1);
                setCurrentPage(response.pagination?.currentPage || 1);
            } else {
                console.error('Failed to fetch memberships:', response.message);
                setMembers([]);
                setTotalItems(0);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Error fetching memberships:', error);
            setMembers([]);
            setTotalItems(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    // Fetch single membership details
    const fetchMembershipDetails = async (uuid) => {
        try {
            setViewLoading(true);

            const response = await getMembershipById(uuid);

            if (response.success) {
                const membership = response.data;

                const memberDetail = {
                    // =========================
                    // Membership Details
                    // =========================
                    id: membership._id,
                    uuid: membership.uuid,
                    userUuid: membership.userUuid,
                    plan: membership.plan || "",
                    startDate: membership.startDate || null,
                    endDate: membership.endDate || null,
                    createdAt: membership.createdAt || null,

                    // =========================
                    // User Details
                    // =========================
                    name: membership.user?.fullName || "Unknown",
                    email: membership.user?.email || "N/A",
                    phone: membership.user?.mobileNumber || "N/A",
                    profileImage: membership.user?.profileImage || "",
                    type: membership.user?.type || "Member",
                    country: membership.user?.country || "N/A",
                    gender: membership.user?.gender || "N/A",

                    // User Status
                    userIsActive: membership.user?.isActive ?? false,

                    // Complete user object
                    user: membership.user || null,
                };

                setSelectedMember(memberDetail);
                setViewMemberModal(true);
            } else {
                console.error(
                    "Failed to fetch membership details:",
                    response.message
                );
            }
        } catch (error) {
            console.error("Error fetching membership details:", error);
        } finally {
            setViewLoading(false);
        }
    };
    // Initial load and when search/page changes
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            fetchMemberships(currentPage, searchTerm);
        }, 300); // Debounce search

        return () => clearTimeout(delayDebounce);
    }, [currentPage, searchTerm]);

    const getFilteredMembers = () => {
        let filtered = members;
        if (planFilter !== "all") {
            filtered = filtered.filter(
                (member) => (member.plan || "").toLowerCase() === planFilter.toLowerCase()
            );
        }
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (member) =>
                    member.name.toLowerCase().includes(lowerSearch) ||
                    member.email.toLowerCase().includes(lowerSearch) ||
                    member.phone.includes(searchTerm)
            );
        }
        return filtered;
    };

    const filteredMembers = getFilteredMembers();
    const displayTotalItems = filteredMembers.length;
    const displayTotalPages = Math.ceil(displayTotalItems / ITEMS_PER_PAGE);

    // Get current page data
    const getCurrentPageData = () => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredMembers.slice(startIndex, endIndex);
    };

    const currentMembers = getCurrentPageData();

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const openViewModal = (member) => {
        setSelectedMember(member);
        setViewMemberModal(true);
        // Fetch full details in background
        fetchMembershipDetails(member.uuid);
    };

    const closeViewModal = () => {
        setViewMemberModal(false);
        setSelectedMember(null);
    };

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";

        return new Date(dateString).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };


    return (
        <>
            <CommonHeader />

            <Container className="mt--7" fluid>
                <Row>
                    <Col>
                        <Card className="shadow border-0">
                            <CardHeader className="bg-white border-0 pb-0 pt-4">
                                <Row className="align-items-center">
                                    <Col>
                                        <h3 className="mb-0 font-weight-bold text-dark">
                                            Membership Management
                                        </h3>
                                    </Col>
                                </Row>
                            </CardHeader>

                            {/* ================= BODY ================= */}
                            <CardBody>
                                {/* SEARCH & FILTER */}
                                <Row className="align-items-start align-items-lg-center mb-3 px-3 flex-column flex-lg-row" style={{ gap: "12px" }}>
                                    <Col lg="5" className="px-0">
                                        <InputGroup>
                                            <InputGroupAddon addonType="prepend">
                                                <InputGroupText>
                                                    <i className="fas fa-search" />
                                                </InputGroupText>
                                            </InputGroupAddon>

                                            <Input
                                                type="search"
                                                placeholder="Search members by name, email, or phone..."
                                                value={searchTerm}
                                                onChange={(e) => {
                                                    setSearchTerm(e.target.value);
                                                    setCurrentPage(1);
                                                }}
                                            />
                                        </InputGroup>
                                    </Col>

                                    <Col lg="3" className="px-0">
                                        <CustomDropdown
                                            value={planFilter}
                                            onChange={(val) => {
                                                setPlanFilter(val);
                                                setCurrentPage(1);
                                            }}
                                            options={[
                                                { label: "All Plans", value: "all" },
                                                { label: "Monthly", value: "monthly" },
                                                { label: "Yearly", value: "yearly" },
                                            ]}
                                        />
                                    </Col>
                                </Row>

                                {/* TABLE */}
                                <div className="table-responsive">
                                    <Table className="align-items-center table-flush" responsive>
                                        <thead className="thead-light">
                                            <tr>
                                                <th>Sr. No.</th>
                                                <th>Member</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                                <th>Plan</th>
                                                <th className="text-right">Actions</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {loading && currentMembers.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-5">
                                                        <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader" />
                                                        <p className="mt-2 text-muted mb-0">
                                                            Loading members...
                                                        </p>
                                                    </td>
                                                </tr>
                                            ) : currentMembers.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-5">
                                                        <p className="text-muted mb-0">
                                                            {searchTerm || planFilter !== "all"
                                                                ? "No members match your search or filter."
                                                                : "No members registered yet."}
                                                        </p>
                                                    </td>
                                                </tr>
                                            ) : (
                                                currentMembers.map((member, index) => (
                                                    <tr key={member.uuid}>
                                                        {/* Sr. No. */}
                                                        <td>
                                                            <span className="font-weight-bold text-sm">
                                                                {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                                                            </span>
                                                        </td>

                                                        {/* Member */}
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="guide-avatar mr-2">
                                                                    {member.user?.profileImage ? (
                                                                        <img
                                                                            src={`https://areatrek-live-bucket.s3.us-east-2.amazonaws.com/users-profile-images/${member.user.profileImage}`}
                                                                            alt={member.user?.fullName || "Member"}
                                                                            style={{
                                                                                width: "40px",
                                                                                height: "40px",
                                                                                borderRadius: "50%",
                                                                                objectFit: "cover",
                                                                            }}
                                                                            onError={(e) => {
                                                                                e.target.style.display = "none";
                                                                                e.target.parentElement.textContent =
                                                                                    member.user?.fullName?.charAt(0) || "U";
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        member.user?.fullName?.charAt(0) || "U"
                                                                    )}
                                                                </div>

                                                                <div>
                                                                    <div className="font-weight-bold text-dark">
                                                                        {member.user?.fullName || "Unknown"}
                                                                    </div>

                                                                    <small className="text-muted">
                                                                        {member.user?.type || "Member"}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        {/* Email */}
                                                        <td>
                                                            <span className="text-sm text-dark">
                                                                {member.email}
                                                            </span>
                                                        </td>

                                                        {/* Phone */}
                                                        <td>
                                                            <span className="text-sm text-muted">
                                                                {member.phone}
                                                            </span>
                                                        </td>

                                                        {/* Plan */}
                                                        <td>
                                                            <Badge
                                                                className="badge-default"
                                                                color={
                                                                    member.plan === "yearly"
                                                                        ? "warning"
                                                                        : "info"
                                                                }
                                                                pill
                                                            >
                                                                <i
                                                                    className={`fas ${member.plan === "yearly"
                                                                        ? "fa-calendar-check"
                                                                        : "fa-calendar-alt"
                                                                        } mr-1`}
                                                                />
                                                                {member.plan
                                                                    ? member.plan.charAt(0).toUpperCase() +
                                                                    member.plan.slice(1)
                                                                    : "N/A"}
                                                            </Badge>
                                                        </td>

                                                        {/* Actions */}
                                                        <td className="text-right">
                                                            <UncontrolledDropdown className="table-action-dropdown">
                                                                <DropdownToggle
                                                                    className="btn-icon-only text-light"
                                                                    color=""
                                                                    role="button"
                                                                    size="sm"
                                                                >
                                                                    <i className="fas fa-ellipsis-v" />
                                                                </DropdownToggle>
                                                                <DropdownMenu right className="shadow border-0">
                                                                    <DropdownItem
                                                                        onClick={() => openViewModal(member)}
                                                                        disabled={loading}
                                                                    >
                                                                        <i className="fas fa-eye text-info mr-2" />
                                                                        View Details
                                                                    </DropdownItem>
                                                                </DropdownMenu>
                                                            </UncontrolledDropdown>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>

                            {/* ================= FOOTER ================= */}
                            {!loading && displayTotalItems > 0 && (
                                <CardFooter className="py-4 border-0">
                                    <CustomPagination
                                        currentPage={currentPage}
                                        totalPages={displayTotalPages}
                                        totalItems={displayTotalItems}
                                        itemsPerPage={ITEMS_PER_PAGE}
                                        onPageChange={handlePageChange}
                                        itemName="memberships"
                                    />
                                </CardFooter>
                            )}
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* =====================================================
                VIEW MEMBER DETAIL MODAL
            ===================================================== */}
            <Modal
                isOpen={viewMemberModal}
                toggle={closeViewModal}
                centered
                size="lg"
                returnFocus={false}
                className="user-table-user-model membership-view-modal"
            >
                {/* ================= MODAL HEADER ================= */}
                <ModalHeader toggle={closeViewModal}>
                    <div className="user-modal-header-content">
                        <div className="user-modal-header-icon">
                            {selectedMember?.profileImage || selectedMember?.user?.profileImage ? (
                                <img
                                    src={`https://areatrek-live-bucket.s3.us-east-2.amazonaws.com/users-profile-images/${selectedMember.profileImage || selectedMember.user?.profileImage}`}
                                    className="user-modal-header-img"
                                    alt={selectedMember.name || "Member"}
                                    onError={(e) => {
                                        e.target.style.display = "none";
                                        const fallback = e.target.parentElement?.querySelector("span");
                                        if (fallback) fallback.style.display = "inline-block";
                                    }}
                                />
                            ) : (
                                <span>
                                    {selectedMember?.name?.charAt(0)?.toUpperCase() || "M"}
                                </span>
                            )}
                        </div>

                        <div>
                            <h4 className="mb-0">
                                {selectedMember?.name || "Member Details"}
                            </h4>
                            <small>Membership Profile Details</small>
                        </div>
                    </div>
                </ModalHeader>

                {/* ================= MODAL BODY ================= */}
                <ModalBody>
                    {viewLoading ? (
                        <div className="text-center py-5">
                            <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                            <p className="mt-2 text-muted mb-0">
                                Loading member details...
                            </p>
                        </div>
                    ) : selectedMember ? (
                        <>
                            {/* =====================================================
                                PROFILE SECTION (NO COVER IMAGE)
                            ====================================================== */}
                            <div className="user-profile-hero no-cover text-center my-3">
                                {/* Profile Image */}
                                <div className="user-modal-avatar avatar-without-cover mb-2">
                                    {selectedMember.profileImage || selectedMember.user?.profileImage ? (
                                        <img
                                            src={`https://areatrek-live-bucket.s3.us-east-2.amazonaws.com/users-profile-images/${selectedMember.profileImage || selectedMember.user?.profileImage}`}
                                            alt={selectedMember.name || "Member"}
                                            className="user-modal-profile-image"
                                            onError={(e) => {
                                                e.currentTarget.style.display = "none";
                                                const fallback = e.currentTarget.parentElement?.querySelector(
                                                    ".profile-image-fallback"
                                                );
                                                if (fallback) {
                                                    fallback.style.display = "flex";
                                                }
                                            }}
                                        />
                                    ) : null}

                                    {/* First Letter Fallback */}
                                    <span
                                        className="profile-image-fallback"
                                        style={{
                                            display: (selectedMember.profileImage || selectedMember.user?.profileImage)
                                                ? "none"
                                                : "flex",
                                        }}
                                    >
                                        {selectedMember.name?.charAt(0)?.toUpperCase() || "M"}
                                    </span>
                                </div>
                                <h3 className="font-weight-bold text-dark mb-1">
                                    {selectedMember.name || "Unknown"}
                                </h3>
                                <p className="text-muted mb-2">
                                    {selectedMember.email || "No Email"}
                                </p>
                                <div className="d-flex justify-content-center align-items-center flex-wrap" style={{ gap: "8px" }}>
                                    {selectedMember.type && (
                                        <Badge className="custom-badge badge-active" pill>
                                            <i className="fas fa-user-tag mr-1" />
                                            {selectedMember.type.toUpperCase()}
                                        </Badge>
                                    )}
                                    {selectedMember.plan && (
                                        <Badge
                                            color={selectedMember.plan === "yearly" ? "warning" : "info"}
                                            pill
                                            className="px-3 py-1"
                                        >
                                            <i className={`fas ${selectedMember.plan === "yearly" ? "fa-calendar-check" : "fa-calendar-alt"} mr-1`} />
                                            {selectedMember.plan.toUpperCase()} PLAN
                                        </Badge>
                                    )}
                                </div>
                            </div>

                            {/* =====================================================
                                BASIC INFORMATION
                            ====================================================== */}
                            <div className="user-modal-section mb-4">
                                <div className="user-modal-section-title">
                                    <i className="fas fa-user"></i>
                                    <span>Basic Information</span>
                                </div>

                                <Row>
                                    {/* Full Name */}
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-user"></i>
                                            </div>
                                            <div className="user-detail-content">
                                                <span>Full Name</span>
                                                <strong>
                                                    {selectedMember.name || "No Data Found"}
                                                </strong>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Email */}
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-envelope"></i>
                                            </div>
                                            <div className="user-detail-content">
                                                <span>Email</span>
                                                <strong>
                                                    {selectedMember.email || "No Data Found"}
                                                </strong>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Phone */}
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-phone"></i>
                                            </div>
                                            <div className="user-detail-content">
                                                <span>Phone Number</span>
                                                <strong>
                                                    {selectedMember.phone || "No Data Found"}
                                                </strong>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Member Type */}
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-user-tag"></i>
                                            </div>
                                            <div className="user-detail-content">
                                                <span>Member Type</span>
                                                {selectedMember.type ? (
                                                    <Badge className="custom-badge badge-active" pill>
                                                        {selectedMember.type}
                                                    </Badge>
                                                ) : (
                                                    <strong>No Data Found</strong>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* =====================================================
                                MEMBERSHIP DETAILS
                            ====================================================== */}
                            <div className="user-modal-section mb-4">
                                <div className="user-modal-section-title">
                                    <i className="fas fa-id-card"></i>
                                    <span>Membership Details</span>
                                </div>

                                <Row>
                                    {/* Membership Plan */}
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-crown"></i>
                                            </div>
                                            <div className="user-detail-content">
                                                <span>Membership Plan</span>
                                                {selectedMember.plan ? (
                                                    <Badge
                                                        color={selectedMember.plan === "yearly" ? "warning" : "info"}
                                                        pill
                                                    >
                                                        <i className="fas fa-calendar-check mr-1"></i>
                                                        {selectedMember.plan.charAt(0).toUpperCase() + selectedMember.plan.slice(1)}
                                                    </Badge>
                                                ) : (
                                                    <strong>No Data Found</strong>
                                                )}
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Member Since */}
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-calendar-alt"></i>
                                            </div>
                                            <div className="user-detail-content">
                                                <span>Member Since</span>
                                                <strong>
                                                    {selectedMember.createdAt
                                                        ? formatDate(selectedMember.createdAt)
                                                        : "No Data Found"}
                                                </strong>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Start Date */}
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-calendar-plus"></i>
                                            </div>
                                            <div className="user-detail-content">
                                                <span>Start Date</span>
                                                <strong>
                                                    {selectedMember.startDate
                                                        ? formatDate(selectedMember.startDate)
                                                        : "No Data Found"}
                                                </strong>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* End Date */}
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-calendar-times"></i>
                                            </div>
                                            <div className="user-detail-content">
                                                <span>End Date</span>
                                                <strong>
                                                    {selectedMember.endDate
                                                        ? formatDate(selectedMember.endDate)
                                                        : "No Data Found"}
                                                </strong>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </>
                    ) : (
                        <div className="user-empty-state text-center py-5">
                            <i className="fas fa-user-slash fa-2x text-muted mb-2"></i>
                            <p className="text-muted">No member data available</p>
                        </div>
                    )}
                </ModalBody>

                {/* ================= MODAL FOOTER ================= */}
                <ModalFooter>
                    <Button
                        className="user-action-btn user-view-btn"
                        size="sm"
                        onClick={closeViewModal}
                        color="secondary"
                    >
                        <i className="fas fa-times mr-1"></i>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default Membership;